// Shared chrome used by every screen port: host-space + topbar + rail +
// main content area. Lifted from the approved HTML (host-space, topbar,
// shell, rail, main) and preserved as React.
//
// W1B: the chrome's identity row is driven by the authenticated rep from
// the HubSpot session (via useRep). The old hard-coded JAMIE fixture is
// gone; it displayed the same name regardless of who was signed in,
// which was an attribution risk once this app ships. When the session
// has not resolved yet the rail renders a loading affordance in the rep
// slot. Start / End Working controls sit next to the avatar so the rep's
// working state is one click away from any screen.

import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { IconSprite, Icon } from './IconSprite.js';
import { getReviewReturnTarget } from './ReviewState.js';
import { useRep, type Rep, type RepAvailability } from './useRep.js';
import './chrome.css';

export type RailKey = 'my-work' | 'my-activity' | 'manager' | 'help';

export interface WorkspaceChromeProps {
  rail: RailKey;
  /**
   * W40.2 (2026-10-21): the screen key rendered as `data-screen` on the
   * root. Every screen stylesheet is scoped to `[data-screen=<key>]` (see
   * the generated styles.css headers), so a route only receives its own
   * approved CSS; states rendered without a key (help, route boundaries)
   * get the shared chrome and the parity sheet only.
   */
  screen?: string;
  children: ReactNode;
}

const RAIL_DESTINATION: Record<RailKey, string> = {
  'my-work': '/',
  'my-activity': '/my-activity',
  // Manager rail resolves to Team Live, with /manager preserved as a
  // legacy redirect in App.tsx so existing bookmarks still work.
  manager: '/team-live',
  help: '/help',
};

export function WorkspaceChrome({ rail, screen, children }: WorkspaceChromeProps): ReactNode {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const customerId = params.customerId ?? params.contactId ?? null;

  const go = useCallback(
    (key: RailKey) => {
      if (key === 'my-work') {
        const target = getReviewReturnTarget(location.state, customerId);
        navigate(target.path, { state: target.state });
        return;
      }
      const source = customerId
        ? {
            ...(location.state && typeof location.state === 'object'
              ? location.state
              : {}),
            returnTo: location.pathname + location.search,
          }
        : location.state;
      navigate(RAIL_DESTINATION[key], {
        state: getReviewReturnTarget(source, customerId).state,
      });
    },
    [customerId, location.pathname, location.search, location.state, navigate],
  );

  return (
    <div className="workspace-screen" data-screen={screen}>
      <IconSprite />
      <div
        className="host-space"
        aria-label="40 pixel space reserved for the HubSpot host bar"
      />
      <header className="topbar">
        <div className="top-left">
          <div className="brand">
            100<b>X</b>
            <small>EQUINE</small>
          </div>
          <span className="divider" />
          <span className="workspace">Sales Workspace</span>
        </div>
        <RepPill />
      </header>
      <div className="shell">
        <aside className="rail" aria-label="Workspace navigation">
          <button
            className={rail === 'my-work' ? 'current' : undefined}
            aria-current={rail === 'my-work' ? 'page' : undefined}
            onClick={() => go('my-work')}
            type="button"
          >
            <Icon id="work" />
            My work
          </button>
          <button
            className={rail === 'my-activity' ? 'current' : undefined}
            aria-current={rail === 'my-activity' ? 'page' : undefined}
            onClick={() => go('my-activity')}
            type="button"
          >
            <Icon id="chart" />
            My Activity
          </button>
          <button
            className={rail === 'manager' ? 'current' : undefined}
            aria-current={rail === 'manager' ? 'page' : undefined}
            onClick={() => go('manager')}
            type="button"
          >
            <Icon id="people" />
            Manager
          </button>
          <button
            className={`bottom${rail === 'help' ? ' current' : ''}`}
            aria-current={rail === 'help' ? 'page' : undefined}
            onClick={() => go('help')}
            type="button"
          >
            <Icon id="help" />
            Help
          </button>
        </aside>
        <main className="main">{children}</main>
      </div>
    </div>
  );
}

// --- Rep pill ------------------------------------------------------------

function RepPill(): ReactNode {
  const state = useRep();
  if (state.status === 'loading') {
    return (
      <div className="rep" aria-busy="true" aria-label="Loading rep identity">
        <span className="available" aria-live="polite">
          <i className="dot" aria-hidden="true" />
          Loading…
        </span>
      </div>
    );
  }
  if (state.status === 'unauthenticated') {
    return (
      <div className="rep" aria-label="No rep signed in">
        <span className="available">
          <i className="dot" aria-hidden="true" />
          Not signed in
        </span>
      </div>
    );
  }
  return <SignedInRep rep={state.rep} />;
}

function SignedInRep({ rep }: { rep: Rep }): ReactNode {
  const [working, setWorking] = useState<boolean>(
    rep.availability === 'available' || rep.availability === 'on-call',
  );
  const toggleWorking = useCallback(() => {
    // Start / End Working button: optimistic client-side toggle. Server
    // persistence lands with the shift-planning adapter in a later packet;
    // the control itself is required by the approved chrome so the rep
    // can see what state they are in without leaving the screen.
    setWorking((prev) => !prev);
  }, []);
  const availabilityLabel = formatAvailability(
    working ? 'available' : 'off-shift',
  );
  return (
    <div className="rep">
      <span className="available" aria-live="polite">
        <i className="dot" aria-hidden="true" />
        {availabilityLabel}
      </span>
      <button
        type="button"
        className="rep-working"
        aria-pressed={working}
        onClick={toggleWorking}
      >
        {working ? 'End Working' : 'Start Working'}
      </button>
      <div className="avatar" aria-hidden="true">
        {rep.initials}
      </div>
      <span aria-label={`Signed in as ${rep.fullName}`}>{rep.fullName}</span>
    </div>
  );
}

function formatAvailability(av: RepAvailability): string {
  switch (av) {
    case 'available':
      return 'Available';
    case 'on-call':
      return 'On call';
    case 'off-shift':
      return 'Off shift';
    case 'unknown':
    default:
      return 'Unknown';
  }
}
